import React, { useState, useMemo } from 'react';
import './DashboardHistory.css';

function DashboardHistory({ qrHistory = [], onRefresh, onDelete, userRole }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [selectedQRs, setSelectedQRs] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [qrToDelete, setQrToDelete] = useState(null);

  // Garantir que qrHistory seja sempre um array
  const safeQrHistory = Array.isArray(qrHistory) ? qrHistory : [];

  // Tipos de QR disponíveis
  const qrTypes = [
    'URL', 'EMAIL', 'PHONE', 'SMS', 'WIFI', 'VCARD', 
    'GEOLOCATION', 'EVENT', 'WHATSAPP', 'PAYPAL', 'BITCOIN', 'PIX'
  ];

  // Filtrar e ordenar QR codes
  const filteredAndSortedQRs = useMemo(() => {
    let result = [...safeQrHistory];

    // Filtro de busca
    if (searchTerm) {
      result = result.filter(qr => {
        const config = qr.config || {};
        const searchLower = searchTerm.toLowerCase();
        return (
          (config.data || '').toLowerCase().includes(searchLower) ||
          (qr.type || '').toLowerCase().includes(searchLower) ||
          (qr.id?.toString() || '').includes(searchLower)
        );
      });
    }

    // Filtro por tipo
    if (filterType !== 'all') {
      result = result.filter(qr => qr.type === filterType);
    }

    // Filtro por status
    if (filterStatus !== 'all') {
      result = result.filter(qr => qr.status === filterStatus);
    }

    // Ordenação
    result.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date-asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        default:
          return 0;
      }
    });

    return result;
  }, [safeQrHistory, searchTerm, filterType, filterStatus, sortBy]);

  // Toggle seleção de QR
  const toggleSelectQR = (id) => {
    setSelectedQRs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Selecionar/desselecionar todos
  const toggleSelectAll = () => {
    if (selectedQRs.size === filteredAndSortedQRs.length) {
      setSelectedQRs(new Set());
    } else {
      setSelectedQRs(new Set(filteredAndSortedQRs.map(qr => qr.id)));
    }
  };

  // Confirmar exclusão
  const handleDeleteClick = (qr) => {
    setQrToDelete(qr);
    setShowDeleteModal(true);
  };

  // Executar exclusão
  const confirmDelete = async () => {
    if (qrToDelete && onDelete) {
      await onDelete(qrToDelete.id);
      setShowDeleteModal(false);
      setQrToDelete(null);
    }
  };

  // Deletar múltiplos QRs
  const handleBulkDelete = async () => {
    if (selectedQRs.size > 0 && onDelete) {
      for (const id of selectedQRs) {
        await onDelete(id);
      }
      setSelectedQRs(new Set());
    }
  };

  // Formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obter emoji do tipo
  const getTypeEmoji = (type) => {
    const emojis = {
      'URL': '🔗',
      'EMAIL': '✉️',
      'PHONE': '📱',
      'SMS': '💬',
      'WIFI': '📶',
      'VCARD': '👤',
      'GEOLOCATION': '📍',
      'EVENT': '📅',
      'WHATSAPP': '💚',
      'PAYPAL': '💰',
      'BITCOIN': '₿',
      'PIX': '🇧🇷'
    };
    return emojis[type] || '📄';
  };

  // Download QR code
  const handleDownload = (qr) => {
    // Implementar download - pode reutilizar lógica do QRGenerator
    console.log('Download QR:', qr.id);
  };

  return (
    <div className="history-container">
      {/* Header com ações */}
      <div className="history-header">
        <div className="history-title-section">
          <h2>📚 Meu Histórico de QR Codes</h2>
          <p>Gerencie todos os seus QR codes criados</p>
        </div>
        <div className="history-actions">
          <button className="btn-refresh" onClick={onRefresh}>
            <span className="refresh-icon">🔄</span>
            Atualizar
          </button>
          {selectedQRs.size > 0 && (
            <button className="btn-bulk-delete" onClick={handleBulkDelete}>
              🗑️ Excluir {selectedQRs.size} selecionado{selectedQRs.size > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="history-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="🔍 Buscar por conteúdo, tipo ou ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-selects">
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">📋 Todos os tipos</option>
            {qrTypes.map(type => (
              <option key={type} value={type}>{getTypeEmoji(type)} {type}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">🏷️ Todos os status</option>
            <option value="active">✅ Ativos</option>
            <option value="inactive">⏸️ Inativos</option>
            <option value="expired">⏱️ Expirados</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date-desc">📅 Mais recentes</option>
            <option value="date-asc">📅 Mais antigos</option>
            <option value="type">🔤 Por tipo</option>
            <option value="status">🏷️ Por status</option>
          </select>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="history-stats">
        <div className="stat-card">
          <span className="stat-value">{safeQrHistory.length}</span>
          <span className="stat-label">Total de QRs</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredAndSortedQRs.length}</span>
          <span className="stat-label">Filtrados</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{safeQrHistory.filter(q => q.status === 'active').length}</span>
          <span className="stat-label">Ativos</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{selectedQRs.size}</span>
          <span className="stat-label">Selecionados</span>
        </div>
      </div>

      {/* Tabela de QR Codes */}
      {filteredAndSortedQRs.length === 0 ? (
        <div className="history-empty">
          <div className="empty-icon">📭</div>
          <h3>Nenhum QR Code encontrado</h3>
          <p>
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'Tente ajustar os filtros ou fazer uma nova busca.'
              : 'Você ainda não criou nenhum QR Code. Comece agora!'}
          </p>
        </div>
      ) : (
        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedQRs.size === filteredAndSortedQRs.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Tipo</th>
                <th>Conteúdo</th>
                <th>Status</th>
                <th>Criado em</th>
                <th>Expira em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedQRs.map(qr => (
                <tr key={qr.id} className={selectedQRs.has(qr.id) ? 'selected' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedQRs.has(qr.id)}
                      onChange={() => toggleSelectQR(qr.id)}
                    />
                  </td>
                  <td>
                    <span className="qr-type-badge">
                      {getTypeEmoji(qr.type)} {qr.type}
                    </span>
                  </td>
                  <td className="qr-content">
                    {qr.config?.data ? (
                      qr.config.data.length > 50 
                        ? qr.config.data.substring(0, 50) + '...' 
                        : qr.config.data
                    ) : 'N/A'}
                  </td>
                  <td>
                    <span className={`status-badge status-${qr.status}`}>
                      {qr.status === 'active' && '✅ Ativo'}
                      {qr.status === 'inactive' && '⏸️ Inativo'}
                      {qr.status === 'expired' && '⏱️ Expirado'}
                    </span>
                  </td>
                  <td>{formatDate(qr.createdAt)}</td>
                  <td>{qr.expiresAt ? formatDate(qr.expiresAt) : '∞ Nunca'}</td>
                  <td>
                    <div className="qr-actions">
                      <button 
                        className="btn-action btn-download" 
                        onClick={() => handleDownload(qr)}
                        title="Download"
                      >
                        ⬇️
                      </button>
                      <button 
                        className="btn-action btn-delete" 
                        onClick={() => handleDeleteClick(qr)}
                        title="Excluir"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir este QR Code?</p>
            {qrToDelete && (
              <div className="modal-qr-info">
                <strong>Tipo:</strong> {getTypeEmoji(qrToDelete.type)} {qrToDelete.type}
                <br />
                <strong>Criado em:</strong> {formatDate(qrToDelete.createdAt)}
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-modal-cancel" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </button>
              <button className="btn-modal-confirm" onClick={confirmDelete}>
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardHistory;
